/**
 * Utility functions for workflow management and capability detection
 */

/**
 * Check if a workflow schema accepts image inputs
 * @param {Object} schema - The workflow schema object
 * @returns {boolean} - True if workflow accepts images
 */
export const workflowAcceptsImages = (schema) => {
  if (!schema || typeof schema !== 'object') return false;
  
  // Check each field in the schema for image input capability
  return Object.values(schema).some(field => {
    if (!field) return false;
    
    // Check for explicit image upload field
    if (field.type === 'b64data' && field.image_upload === true) {
      return true;
    }
    
    // Check for image-related field names
    if (field.type === 'b64data' && field.title) {
      const title = field.title.toLowerCase();
      return title.includes('image') || 
             title.includes('img') || 
             title.includes('picture') ||
             title.includes('photo');
    }
    
    // Check for input_image field specifically
    if (field.name === 'input_image' || field.id === 'input_image') {
      return true;
    }
    
    return false;
  });
};

/**
 * Get the first available image-compatible workflow from a list
 * @param {Array} workflows - Array of workflow objects with schemas
 * @returns {Object|null} - First compatible workflow or null
 */
export const getFirstImageCompatibleWorkflow = (workflows) => {
  if (!Array.isArray(workflows)) return null;
  
  return workflows.find(workflow => 
    workflowAcceptsImages(workflow.schema || workflow)
  ) || null;
};

/**
 * Get user's preferred image editing workflow from localStorage
 * @returns {Object|null} - Preferred workflow data or null
 */
export const getPreferredImageWorkflow = () => {
  try {
    const stored = localStorage.getItem('preferred_image_workflow');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading preferred image workflow:', error);
    return null;
  }
};

/**
 * Save user's preferred image editing workflow to localStorage
 * @param {Object} workflow - Workflow data to save
 */
export const savePreferredImageWorkflow = (workflow) => {
  try {
    if (workflow && typeof workflow === 'object') {
      localStorage.setItem('preferred_image_workflow', JSON.stringify({
        id: workflow.id || workflow.name,
        name: workflow.name,
        model: workflow.model,
        timestamp: Date.now()
      }));
    }
  } catch (error) {
    console.error('Error saving preferred image workflow:', error);
  }
};

/**
 * Check if a model name suggests image editing capability
 * @param {string} modelName - Name of the model
 * @returns {boolean} - True if model name suggests image capability
 */
export const modelNameSuggestsImageCapability = (modelName) => {
  if (!modelName || typeof modelName !== 'string') return false;
  
  const name = modelName.toLowerCase();
  const imageKeywords = [
    'img2img',
    'image-to-image',
    'inpaint',
    'outpaint',
    'edit',
    'pix2pix',
    'controlnet',
    'depth',
    'canny',
    'openpose',
    'scribble',
    'seg',
    'mlsd',
    'normal',
    'lineart',
    'anime',
    'shuffle',
    'instruct'
  ];
  
  return imageKeywords.some(keyword => name.includes(keyword));
};

/**
 * Get the last used image editing workflow from localStorage
 * @returns {Object|null} - Last used workflow or null
 */
export const getLastUsedImageWorkflow = () => {
  try {
    const stored = localStorage.getItem('last_used_image_workflow');
    if (!stored) return null;
    
    const workflow = JSON.parse(stored);
    // Check if workflow was used in last 30 days
    const thirtyDaysAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
    if (workflow.timestamp && workflow.timestamp > thirtyDaysAgo) {
      return workflow;
    }
    return null;
  } catch (error) {
    console.error('Error reading last used image workflow:', error);
    return null;
  }
};

/**
 * Save the last used image editing workflow
 * @param {Object} workflow - Workflow data to save
 */
export const saveLastUsedImageWorkflow = (workflow) => {
  try {
    if (workflow && typeof workflow === 'object') {
      localStorage.setItem('last_used_image_workflow', JSON.stringify({
        ...workflow,
        timestamp: Date.now()
      }));
    }
  } catch (error) {
    console.error('Error saving last used image workflow:', error);
  }
};

/**
 * Clear workflow switching flag from localStorage
 */
export const clearWorkflowSwitchFlag = () => {
  localStorage.removeItem('workflow_switch_required');
  localStorage.removeItem('original_workflow_before_switch');
};

/**
 * Set workflow switching flag in localStorage
 * @param {Object} originalWorkflow - The workflow that was active before switching
 */
export const setWorkflowSwitchFlag = (originalWorkflow) => {
  localStorage.setItem('workflow_switch_required', 'true');
  if (originalWorkflow) {
    localStorage.setItem('original_workflow_before_switch', JSON.stringify(originalWorkflow));
  }
};

/**
 * Check if workflow switching is required
 * @returns {boolean} - True if workflow switch is required
 */
export const isWorkflowSwitchRequired = () => {
  return localStorage.getItem('workflow_switch_required') === 'true';
};

/**
 * Get the original workflow before switching
 * @returns {Object|null} - Original workflow or null
 */
export const getOriginalWorkflow = () => {
  try {
    const stored = localStorage.getItem('original_workflow_before_switch');
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error('Error reading original workflow:', error);
    return null;
  }
};